import styled from 'styled-components';

import { useTheme } from '@/services/theme';
import { CuratorID } from '@/features/vaults/types/CuratorID';
import { CuratorNameInsteadOfLogo } from '@/shared/components/CuratorIcon/ui/CuratorNameInsteadOfLogo';

import { ReactComponent as Steakhouse } from './images/steakhouse.svg';
import { ReactComponent as P2p } from './images/p2p.svg';
import { ReactComponent as Renzo } from './images/renzo.svg';
import { ReactComponent as Re7 } from './images/re7.svg';
import { ReactComponent as K3 } from './images/k3.svg';
import { ReactComponent as Infstones } from './images/infstones.svg';
import { ReactComponent as ChorusOne } from './images/chorusone.svg';
import { ReactComponent as Luganodes } from './images/luganodes.svg';
import { ReactComponent as Rockx } from './images/rockx.svg';
import { ReactComponent as Quasar } from './images/quasar.svg';
import { ReactComponent as MEVCapital } from './images/mevcapital.svg';
import { ReactComponent as Hashkey } from './images/hashkey.svg';
import { ReactComponent as Infrasingularity } from './images/infrasingularity.svg';
import IBTC from './images/ibtc.png';
import { ReactComponent as Hyve } from './images/hyve.svg';
import { ReactComponent as A41 } from './images/a41.svg';
import { ReactComponent as Stakefish } from './images/stakefish.svg';
import { ReactComponent as Gauntlet } from './images/gauntlet.svg';
import { ReactComponent as Republic } from './images/republic.svg';
import { ReactComponent as Proviroll } from './images/proviroll.svg';
import { ReactComponent as DSRV } from './images/dsrv.svg';
import { ReactComponent as Cosmostation } from './images/cosmostation.svg';
import { ReactComponent as NodesGuru } from './images/nodesguru.svg';
import { ReactComponent as StakeCapital } from './images/stakecapital.svg';
import Cp0x from './images/cp0x.png';
import { ReactComponent as Tyr } from './images/tyr-capital.svg';
import { ReactComponent as Bima } from './images/bima.svg';
import { ReactComponent as Symbiosis } from './images/symbiosis.svg';
import { ReactComponent as FlowTraders } from './images/flow-traders.svg';
import { ReactComponent as Node01 } from './images/01node-light.svg';
import { ReactComponent as Fungl } from './images/fungl.svg';
import { ReactComponent as UltraYield } from './images/ultrayield.svg';
import { ReactComponent as EdgeCapital } from './images/edgecapital.svg';
import { ReactComponent as Encapsulate } from './images/encapsulate.svg';
import { ReactComponent as Launchnodes } from './images/launchnodes.svg';
import { ReactComponent as Xhash } from './images/xhash.svg';
import { ReactComponent as CycleNetwork } from './images/cyclenetwork.svg';
import { ReactComponent as Lombard } from './images/lombard.svg';
import { ReactComponent as RuntimeLabs } from './images/runtime_labs.svg';
import { ReactComponent as Mellow } from './images/mellow.svg';
import { ReactComponent as Theoriq } from './images/theoriq.svg';
import { ReactComponent as Alphaping } from './images/alphaping.svg';
import { ReactComponent as SolvFinance } from './images/solv-finance.svg';
import { ReactComponent as Mezo } from './images/mezo.svg';
import { ReactComponent as Sense } from './images/sensecapital_dark.svg';
import { ReactComponent as LiDo } from '../../../pages/LandingPage/sections/Partners/defi/lido.svg';

export const CURATOR_ICONS: Record<CuratorID, JSX.Element> = {
  lido: <LiDo />,
  mellow: <Mellow />,
  // eslint-disable-next-line camelcase
  runtime_labs: <RuntimeLabs />,
  steakhouse: <Steakhouse />,
  p2p: <P2p />,
  renzo: <Renzo />,
  re7labs: <Re7 />,
  nexo: <K3 />,
  infStones: <Infstones />,
  chorusone: <ChorusOne />,
  luganodes: <Luganodes />,
  rockx: <Rockx />,
  quasar: <Quasar />,
  mevcapital: <MEVCapital />,
  hashkey: <Hashkey />,
  infrasingularity: <Infrasingularity />,
  ibtc: <img alt="iBTC" src={IBTC} />,
  hyve: <Hyve style={{ transform: 'scale(0.9)' }} />,
  cp0x: <img alt="Cp0x" src={Cp0x} height={30} width={30} />,
  a41: <A41 style={{ transform: 'scale(0.8)' }} />,
  gauntlet: <Gauntlet />,
  stakefish: <Stakefish />,
  republic: <Republic />,
  proviroll: <Proviroll />,
  dsrv: <DSRV style={{ transform: 'scale(0.8)' }} />,
  cosmostation: <Cosmostation style={{ transform: 'scale(1.4)' }} />,
  'nodes.guru': <NodesGuru />,
  stakecapital: <StakeCapital style={{ transform: 'scale(1.2)', margin: '0 5px' }} />,
  bima: <Bima />,
  symbiosis: <Symbiosis />,
  // eslint-disable-next-line camelcase
  flow_traders: <FlowTraders />,
  fungl: <Fungl />,
  '01node': <Node01 />,
  ultrayield: <UltraYield />,
  edgecapital: <EdgeCapital />,
  encapsulate: <Encapsulate />,
  launchnodes: <Launchnodes />,
  xhash: <Xhash />,
  cyclenetwork: <CycleNetwork />,
  lombard: <Lombard style={{ transform: 'scale(1.6)' }} />,
  theoriq: <Theoriq style={{ transform: 'scale(2.4)', margin: '0 15px' }} />,
  alphaping: <Alphaping style={{ transform: 'scale(0.9)' }} />,
  'solv-finance': <SolvFinance />,
  mezo: <Mezo />,
  sense: <Sense />,
  perseus: <CuratorNameInsteadOfLogo>Perseus Digital</CuratorNameInsteadOfLogo>,
  tyr: <Tyr />,
};

export const CURATORS = Object.values(CURATOR_ICONS);

type Size = 'sm' | 'md';

const iconSizes: Record<Size, number> = {
  sm: 18,
  md: 20,
};

const IconWrapper = styled.div`
  padding: 0;
  display: flex;
  align-items: center;
  width: auto;
  justify-content: center;

  & svg,
  & img {
    height: ${({ size }: { size: Size }) => iconSizes[size]}px !important;
    width: auto;
    object-fit: contain;
    object-position: center;
  }
`;

interface CuratorIconProps extends Omit<React.HTMLProps<HTMLDivElement>, 'size'> {
  curatorId: CuratorID;
  size?: Size;
}

export const CuratorIcon: React.FC<CuratorIconProps> = ({
  curatorId,
  size = 'md',
  ...restProps
}) => {
  const { theme } = useTheme();

  if (curatorId in CURATOR_ICONS) {
    return (
      <IconWrapper theme={theme} size={size} {...restProps}>
        {CURATOR_ICONS[curatorId]}
      </IconWrapper>
    );
  }
  return null;
};
